import { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Skeleton,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  btnStyle,
  ContainerModalBtn,
  formStyle,
  layout,
} from "../styles";
import { Apartamento } from "../../../interfaces/Apartamento";
import { Empreendimento } from "../../../interfaces/Empreendimento";
import { buscarEmpreendimentosService } from "../../../services/buscarEmpreendimentosService";
import { useCadastrarApartamento } from "../../../hooks/useCadastrarApartamento";
import { useError } from "../../../uteis/mensagens/useError";
import { useSuccess } from "../../../uteis/mensagens/useSuccess";

interface ModalAdicionarProps {
  handleFechar: () => void;
  empreendimentoInicialId?: number | null;
  isModalOpen: boolean;
}

export default function ModalAdicionar({
  isModalOpen,
  handleFechar,
  empreendimentoInicialId,
}: ModalAdicionarProps) {
  const [formAp] = Form.useForm();
  const navigate = useNavigate();
  const [empreendimentos, setEmpreendimentos] = useState<Empreendimento[]>([]);
  const [isLoadingEmpreendimentos, setIsLoadingEmpreendimentos] =
    useState(false);
  const { handleSubmit, isLoading } = useCadastrarApartamento();
  const { showError, contextHolder: errorContextHolder } = useError();
  const { showSuccess, contextHolder: successContextHolder } = useSuccess();

  useEffect(() => {
    const buscarEmpreendimentos = async () => {
      if (!isModalOpen) {
        return;
      }

      setIsLoadingEmpreendimentos(true);
      try {
        const data = await buscarEmpreendimentosService();
        setEmpreendimentos(data ?? []);
      } finally {
        setIsLoadingEmpreendimentos(false);
      }
    };

    buscarEmpreendimentos();
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) {
      formAp.resetFields();
      return;
    }

    formAp.setFieldsValue({
      empreendId: empreendimentoInicialId || undefined,
      origem: 0,
      estacionamento: 0,
      terreo: 0,
      varanda: 0,
    });
  }, [empreendimentoInicialId, formAp, isModalOpen]);

  const onFinish = async (values: Apartamento) => {
    try {
      await handleSubmit(values);

      showSuccess("Apartamento cadastrado com sucesso!");
      setTimeout(() => {
        handleFechar();
        navigate(0);
      }, 1500);
    } catch (error) {
      showError("Nao foi possivel cadastrar o apartamento!");
    }
  };

  return (
    <Modal
      open={isModalOpen}
      onCancel={handleFechar}
      footer={null}
      destroyOnClose
    >
      <>{errorContextHolder}</>
      <>{successContextHolder}</>
      {isLoadingEmpreendimentos ? (
        <Skeleton active />
      ) : (
        <>
          <Form
            {...layout}
            form={formAp}
            name="apartamentoCadastro"
            onFinish={onFinish}
            style={formStyle}
          >
            <h1>Apartamento</h1>

            <Form.Item
              name="empreendId"
              label="Empreendimento"
              className="formItems"
              rules={[
                { required: true, message: "Selecione um empreendimento" },
              ]}
            >
              <Select
                options={empreendimentos.map((empreendimento) => ({
                  label: empreendimento.nome,
                  value: empreendimento.id,
                }))}
                placeholder="Selecione um empreendimento"
              />
            </Form.Item>

            <Form.Item
              name="nome"
              label="Apartamento"
              className="formItems"
              rules={[
                { required: true, message: "Informe o nome do apartamento" },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="origem"
              label="Origem"
              className="formItems"
              rules={[{ required: true, message: "Informe a origem" }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              name="capacidade"
              label="Capacidade"
              className="formItems"
              rules={[{ required: true, message: "Informe a capacidade" }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              name="quartos"
              label="Quartos"
              className="formItems"
              rules={[
                { required: true, message: "Informe a quantidade de quartos" },
              ]}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              name="banheiros"
              label="Banheiros"
              className="formItems"
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              name="metragem"
              label="Metragem"
              className="formItems"
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              name="qtdSemanasAno"
              label="Qtd Semanas Ano"
              className="formItems"
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item name="varanda" label="Varanda" className="formItems">
              <Radio.Group>
                <Radio value={0}>Nao definido</Radio>
                <Radio value={1}>Sim</Radio>
                <Radio value={2}>Nao</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name="terreo" label="Terreo" className="formItems">
              <Radio.Group>
                <Radio value={0}>Nao definido</Radio>
                <Radio value={1}>Sim</Radio>
                <Radio value={2}>Nao</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="estacionamento"
              label="Estacionamento"
              className="formItems"
            >
              <Radio.Group>
                <Radio value={0}>Nao definido</Radio>
                <Radio value={1}>Sim</Radio>
                <Radio value={2}>Nao</Radio>
              </Radio.Group>
            </Form.Item>

            <ContainerModalBtn>
              <Button
                htmlType="submit"
                loading={isLoading}
                style={btnStyle}
                size="large"
                type="primary"
              >
                Cadastrar
              </Button>
            </ContainerModalBtn>
          </Form>
        </>
      )}
    </Modal>
  );
}
